import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Users as UsersIcon, Power, Pencil, Plus, Copy, Check, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { CAPABILITIES, CAPABILITY_LABELS } from "@costadorada/shared";
import { ROLE_LABEL, roleLabel, type Role } from "@/lib/roles";

// The roles an account can be given. Older accounts may still carry PUBLIC (shown, not offered).
const ASSIGNABLE_ROLES: Role[] = ["RESIDENT", "BOARD_MEMBER", "ADMIN"];
type BoardPosition = "PRESIDENT" | "VICE_PRESIDENT" | "SECRETARY" | "TREASURER" | "DIRECTOR";

const POSITION_LABEL: Record<BoardPosition, { es: string; en: string }> = {
  PRESIDENT: { es: "Presidente", en: "President" },
  VICE_PRESIDENT: { es: "Vicepresidente", en: "Vice president" },
  SECRETARY: { es: "Secretaria", en: "Secretary" },
  TREASURER: { es: "Tesorero", en: "Treasurer" },
  DIRECTOR: { es: "Directivo", en: "Director" },
};

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role: Role;
  unitId: string | null;
  unit?: { id: string; unitNumber: string; code?: string | null; building?: string | null } | null;
  boardPosition?: BoardPosition | null;
  permissions?: string[];
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
}

interface UnitRow {
  id: string;
  unitNumber: string;
  building?: string | null;
}

/** POST /users response — carries the generated password back to the admin, once. */
interface Issued extends User {
  tempPassword?: string;
  credentialsEmailed?: boolean;
}

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // Inactive accounts are hidden by default (the API does the same) so retired users
  // and cleaned-up duplicates stop cluttering the list.
  const { data: users = [], isLoading, isError } = useQuery({
    queryKey: ["users", { showInactive }],
    queryFn: async () => (await api.get<User[]>(showInactive ? "/users?includeInactive=true" : "/users")).data,
  });

  const needle = search.trim().toLowerCase();
  const visible = needle
    ? users.filter((u) =>
        [u.firstName, u.lastName, u.email, u.phone ?? "", u.unit?.unitNumber ?? "", u.role, roleLabel(u.role, i18n.language), u.boardPosition ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
    : users;

  const { data: units = [] } = useQuery({
    queryKey: ["units-list"],
    queryFn: async () => (await api.get<UnitRow[]>("/units")).data,
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.put(`/users/${id}`, { isActive })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-2xl text-primary-800 flex items-center gap-2">
            <UsersIcon size={22} />
            {t("users.title")}
          </h1>
          <p className="text-sm text-slate-600">
            {t("users.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/admin/roles" className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 rounded text-slate-700 hover:bg-slate-50">
            <Shield size={14} />
            {tr("Ver roles y permisos", "See roles and permissions")}
          </Link>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800"
          >
            <Plus size={14} />
            {t("users.newUser")}
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          className="input max-w-xs"
          placeholder={tr("Buscar por nombre, correo o unidad…", "Search by name, email or unit…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {tr("Mostrar inactivos", "Show inactive")}
        </label>
        <span className="text-xs text-slate-500">
          {visible.length} {tr("usuarios", "users")}
        </span>
      </div>

      <div className="bg-white rounded-lg shadow-elegant border border-sand-200 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-8 text-center text-slate-500">{tr("Cargando…", "Loading…")}</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{tr("Sin usuarios.", "No users.")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-sand-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">{tr("Usuario", "User")}</th>
                <th className="text-left px-4 py-3">{tr("Correo", "Email")}</th>
                <th className="text-left px-4 py-3">{tr("Rol", "Role")}</th>
                <th className="text-left px-4 py-3">{tr("Cargo", "Position")}</th>
                <th className="text-left px-4 py-3">{tr("Unidad", "Unit")}</th>
                <th className="text-left px-4 py-3">{tr("Último acceso", "Last login")}</th>
                <th className="text-left px-4 py-3">{tr("Estado", "Status")}</th>
                <th className="text-right px-4 py-3">{tr("Acciones", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => {
                const unit = u.unit ?? units.find((x) => x.id === u.unitId) ?? null;
                const missingUnit = u.role === "RESIDENT" && !unit;
                return (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-primary-800">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{u.email}</div>
                      {u.phone && <div className="text-xs text-slate-500">{u.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded bg-primary-50 text-primary-700">
                        {roleLabel(u.role, i18n.language)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {u.boardPosition ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-sand-100 text-primary-800">
                          {tr(POSITION_LABEL[u.boardPosition].es, POSITION_LABEL[u.boardPosition].en)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {unit ? (
                        <>
                          {unit.unitNumber}
                          {unit.building ? <span className="text-slate-400"> · {unit.building}</span> : null}
                          {(unit as { code?: string | null }).code ? (
                            <div className="text-xs font-mono text-slate-500">{(unit as { code?: string | null }).code}</div>
                          ) : null}
                        </>
                      ) : missingUnit ? (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                          {tr("Sin unidad", "No unit")}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {u.lastLogin ? formatDate(u.lastLogin, i18n.language) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {u.isActive ? (
                        <span className="text-xs text-emerald-700">{tr("Activo", "Active")}</span>
                      ) : (
                        <span className="text-xs text-red-700">{tr("Inactivo", "Inactive")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        onClick={() => setEditing(u)}
                        className="text-xs px-2.5 py-1 border border-slate-300 rounded hover:bg-slate-50"
                      >
                        <Pencil size={12} className="inline mr-1" />
                        {tr("Editar", "Edit")}
                      </button>
                      <button
                        onClick={() => toggleActive.mutate({ id: u.id, isActive: !u.isActive })}
                        className={`text-xs px-2.5 py-1 rounded ${u.isActive ? "bg-red-600 text-white hover:bg-red-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                      >
                        <Power size={12} className="inline mr-1" />
                        {u.isActive ? tr("Desactivar", "Deactivate") : tr("Activar", "Activate")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <NewUserModal
          units={units}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["users"] });
          }}
          tr={tr}
        />
      )}

      {editing && (
        <EditUserModal
          target={editing}
          units={units}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["users"] });
          }}
          tr={tr}
        />
      )}
    </div>
  );
}

function NewUserModal({
  units,
  onClose,
  onDone,
  tr,
}: {
  units: UnitRow[];
  onClose: () => void;
  onDone: () => void;
  tr: (es: string, en: string) => string;
}) {
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    role: "RESIDENT" as Role,
    unitId: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post("/users", {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone || undefined,
          role: form.role,
          unitId: form.unitId || undefined,
        })
      ).data as Issued,
    // Hold the dialog open on the credentials panel instead of closing: the generated
    // password is shown exactly once and the admin has to be able to read or copy it.
    onSuccess: (data) => setIssued(data),
    onError: (err) => {
      setError(
        (err as AxiosError<{ message: string }>).response?.data?.message ??
          tr("No se pudo crear el usuario.", "Could not create user.")
      );
    },
  });

  if (issued) {
    return (
      <Modal open onClose={onDone} title={tr("Credenciales generadas", "Credentials issued")}>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {tr(
              "La cuenta fue creada. Entregue estas credenciales al usuario — la contraseña no se volverá a mostrar.",
              "The account was created. Give these credentials to the user — the password will not be shown again."
            )}
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2 text-sm">
            <div>
              <span className="text-slate-500">{tr("Correo", "Email")}: </span>
              <span className="font-mono">{issued.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">{tr("Contraseña temporal", "Temporary password")}: </span>
              <span className="font-mono font-medium">{issued.tempPassword}</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(issued.tempPassword ?? "");
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="text-slate-500 hover:text-primary-700"
                title={tr("Copiar", "Copy")}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          {issued.credentialsEmailed ? (
            <p className="text-sm text-green-700">
              {tr(
                "También se enviaron por correo al usuario.",
                "They were also emailed to the user."
              )}
            </p>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {tr(
                "El correo no pudo enviarse. Entregue las credenciales al usuario por otro medio.",
                "The email could not be sent. Deliver the credentials to the user by other means."
              )}
            </p>
          )}
          <div className="flex justify-end pt-2">
            <button
              onClick={onDone}
              className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800"
            >
              {tr("Listo", "Done")}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={tr("Nuevo usuario", "New user")}>
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          {tr(
            "Se generará una contraseña temporal: se mostrará aquí una sola vez y se enviará por correo al nuevo usuario.",
            "A temporary password will be generated: it is shown here once and emailed to the new user."
          )}
        </p>
        <div>
          <label className="label">{tr("Correo electrónico", "Email")}</label>
          <input
            type="email"
            className="input"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{tr("Nombre", "First name")}</label>
            <input
              className="input"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="label">{tr("Apellido", "Last name")}</label>
            <input
              className="input"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              required
            />
          </div>
        </div>
        <div>
          <label className="label">{tr("Teléfono", "Phone")}</label>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </div>
        <div>
          <label className="label">{tr("Rol", "Role")}</label>
          <select
            className="input"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>{tr(ROLE_LABEL[r].es, ROLE_LABEL[r].en)}</option>
            ))}
            {form.role === "PUBLIC" && (
              <option value="PUBLIC" disabled>{tr("Público (ya no se asigna; elija otro)", "Public (no longer assigned; choose another)")}</option>
            )}
          </select>
        </div>
        <div>
          <label className="label">{tr("Unidad asignada", "Assigned unit")}</label>
          <select
            className="input"
            value={form.unitId}
            onChange={(e) => setForm((f) => ({ ...f, unitId: e.target.value }))}
          >
            <option value="">{tr("— Sin asignar —", "— Unassigned —")}</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unitNumber}
                {u.building ? ` · ${u.building}` : ""}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded border-slate-300 hover:bg-slate-50">
            {tr("Cancelar", "Cancel")}
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={create.isPending || !form.email || !form.firstName || !form.lastName}
            className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800 disabled:opacity-60"
          >
            {create.isPending ? tr("Creando…", "Creating…") : tr("Crear usuario", "Create user")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditUserModal({
  target,
  units,
  onClose,
  onDone,
  tr,
}: {
  target: User;
  units: UnitRow[];
  onClose: () => void;
  onDone: () => void;
  tr: (es: string, en: string) => string;
}) {
  const [form, setForm] = useState({
    email: target.email,
    firstName: target.firstName,
    lastName: target.lastName,
    phone: target.phone ?? "",
    role: target.role,
    unitId: target.unitId ?? "",
  });
  const [permissions, setPermissions] = useState<string[]>(target.permissions ?? []);
  const [position, setPosition] = useState<BoardPosition | "">(target.boardPosition ?? "");
  const [error, setError] = useState<string | null>(null);

  function togglePermission(cap: string) {
    setPermissions((prev) => (prev.includes(cap) ? prev.filter((p) => p !== cap) : [...prev, cap]));
  }

  const save = useMutation({
    mutationFn: async () => {
      const saved = (
        await api.put(`/users/${target.id}`, {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone || null,
          role: form.role,
          unitId: form.unitId || null,
          permissions,
        })
      ).data;
      // The board seat has its own endpoint (one holder per seat, audited separately).
      const wanted = form.role === "BOARD_MEMBER" ? position || null : null;
      if (wanted !== (target.boardPosition ?? null) && (wanted !== null || target.boardPosition)) {
        await api.put(`/users/${target.id}/board-position`, { position: wanted });
      }
      return saved;
    },
    onSuccess: onDone,
    onError: (err) => {
      setError(
        (err as AxiosError<{ message: string }>).response?.data?.message ??
          tr("No se pudo guardar.", "Could not save.")
      );
    },
  });

  return (
    <Modal open onClose={onClose} title={tr("Editar usuario", "Edit user")}>
      <div className="space-y-4">
        <div>
          <label className="label">{tr("Correo electrónico", "Email")}</label>
          <input
            type="email"
            className="input"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
          <p className="text-xs text-slate-400 mt-1">
            {tr("El usuario iniciará sesión con el nuevo correo.", "The user will sign in with the new email.")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{tr("Nombre", "First name")}</label>
            <input
              className="input"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">{tr("Apellido", "Last name")}</label>
            <input
              className="input"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <label className="label">{tr("Teléfono", "Phone")}</label>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </div>
        <div>
          <label className="label">{tr("Rol", "Role")}</label>
          <select
            className="input"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>{tr(ROLE_LABEL[r].es, ROLE_LABEL[r].en)}</option>
            ))}
            {form.role === "PUBLIC" && (
              <option value="PUBLIC" disabled>{tr("Público (ya no se asigna; elija otro)", "Public (no longer assigned; choose another)")}</option>
            )}
          </select>
        </div>
        {form.role === "BOARD_MEMBER" && (
          <div>
            <label className="label">{tr("Cargo en la Junta", "Board seat")}</label>
            <select className="input" value={position} onChange={(e) => setPosition(e.target.value as BoardPosition | "")}>
              <option value="">{tr("— Sin cargo —", "— No seat —")}</option>
              {(Object.keys(POSITION_LABEL) as BoardPosition[]).map((p) => (
                <option key={p} value={p}>
                  {tr(POSITION_LABEL[p].es, POSITION_LABEL[p].en)}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              {tr(
                "La Secretaria convoca reuniones; el Tesorero gestiona finanzas automáticamente. Presidente, Vicepresidente, Secretaria y Tesorero: un solo titular.",
                "The Secretary convenes meetings; the Treasurer manages finances automatically. President, Vice president, Secretary and Treasurer: one holder each."
              )}
            </p>
          </div>
        )}
        <div>
          <label className="label">{tr("Unidad asignada", "Assigned unit")}</label>
          <select
            className="input"
            value={form.unitId}
            onChange={(e) => setForm((f) => ({ ...f, unitId: e.target.value }))}
          >
            <option value="">{tr("— Sin asignar —", "— Unassigned —")}</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unitNumber}
                {u.building ? ` · ${u.building}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">{tr("Permisos de captura", "Data-entry permissions")}</label>
          {form.role === "ADMIN" ? (
            <p className="text-sm text-slate-500">{tr("Los administradores tienen todos los permisos.", "Admins have all permissions.")}</p>
          ) : (
            <div className="space-y-2 border border-slate-200 rounded p-3">
              {CAPABILITIES.map((cap) => (
                <label key={cap} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={permissions.includes(cap)}
                    onChange={() => togglePermission(cap)}
                  />
                  <span>{tr(CAPABILITY_LABELS[cap].es, CAPABILITY_LABELS[cap].en)}</span>
                </label>
              ))}
              <p className="text-xs text-slate-400">
                {tr(
                  "Otorgue a directores que colaboran ingresando datos en nombre de residentes.",
                  "Grant to directors who help enter data on behalf of residents."
                )}
              </p>
            </div>
          )}
        </div>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded border-slate-300 hover:bg-slate-50">
            {tr("Cancelar", "Cancel")}
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800 disabled:opacity-60"
          >
            {save.isPending ? tr("Guardando…", "Saving…") : tr("Guardar", "Save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
